import { Money, moneyMultiply } from "@redotech/money";
import { moneyFormat } from "@redotech/money/util";
import ShoppingCartIcon from "@redotech/redo-icons/shopping-cart-03.svg";
import { CheckoutExperienceStep } from "@redotech/redo-model/advanced-flow/advanced-flow-db-parser";
import { RedoBadge } from "@redotech/redo-web/arbiter-components/badge/redo-badge";
import { RedoFeaturedIcon } from "@redotech/redo-web/arbiter-components/featured-icon/redo-featured-icon";
import { Flex } from "@redotech/redo-web/flex";
import { Text } from "@redotech/redo-web/text";
import { isDef } from "@redotech/util/checks";
import { Handle, Position } from "@xyflow/react";
import { memo, useContext, useMemo } from "react";
import { CheckoutTreeAnalyticsContext } from "../../../../conversion/context/checkout-tree-analytics-provider";
import { PartialWithRequiredType } from "../steps";
import { BaseNode } from "./base-node";
import * as css from "./node.module.css";

export interface CheckoutExperienceNodeData {
  step: PartialWithRequiredType<CheckoutExperienceStep>;
  deleteElements: (id: string) => void;
  readonly: boolean;
}

export const CheckoutExperienceNode = memo(function CheckoutExperienceNode({
  id,
  data,
  selected,
}: {
  id: string;
  data: CheckoutExperienceNodeData;
  selected?: boolean | undefined;
}) {
  const { metrics } = useMetrics({ nodeId: id });

  return (
    <Flex dir="column" gap="md">
      <BaseNode
        containerClassName={css.checkoutNode}
        icon={
          <RedoFeaturedIcon color="gray" Icon={ShoppingCartIcon} size="sm" />
        }
        onDelete={data.readonly ? undefined : () => data.deleteElements(id)}
        selected={selected}
        step={data.step}
        title="Checkout experience"
      >
        <Handle id="top" position={Position.Top} type="target" />
        {isDef(data.step.name) && (
          <Flex align="flex-start" dir="column" gap="xs">
            <Text fontSize="xxs" textColor="secondary">
              Name
            </Text>
            <Flex w="full">
              <RedoBadge color="gray" size="xs" text={data.step.name} />
            </Flex>
          </Flex>
        )}
      </BaseNode>
      {data.readonly && metrics.status === "display-metrics" && (
        <MetricsCallout metrics={metrics.value} />
      )}
    </Flex>
  );
});

const MetricsCallout = memo(function MetricsCallout({
  metrics,
}: {
  metrics: Extract<Metrics, { status: "display-metrics" }>["value"];
}) {
  return (
    <Flex
      className={css.metricsCalloutContainer}
      dir="column"
      gap="md"
      p="lg"
      radius="sm"
    >
      <Flex justify="space-between">
        <Text fontSize="xxs" fontWeight="regular" textColor="white">
          Orders {metrics.orders.count}
        </Text>
        <Text fontSize="xxs" fontWeight="regular" textColor="white">
          AOV{" "}
          {metrics.orders.count > 0
            ? moneyFormat(
                moneyMultiply(metrics.revenue, 1 / metrics.orders.count),
              )
            : "-"}
        </Text>
      </Flex>
      <Flex>
        <Text fontSize="xxs" fontWeight="regular" textColor="white">
          Revenue {moneyFormat(metrics.revenue)}
        </Text>
      </Flex>
    </Flex>
  );
});

type Metrics =
  | { status: "pending" }
  | { status: "metrics-not-applicable" }
  | { status: "metrics-not-available" }
  | {
      status: "display-metrics";
      value: { orders: { count: number }; revenue: Money };
    };

function useMetrics({ nodeId }: { nodeId: string }): { metrics: Metrics } {
  const { analyticsForCheckoutNodeByNodeId, checkoutTreeAnalyticsLoad } =
    useContext(CheckoutTreeAnalyticsContext);

  const metrics: Metrics = useMemo(() => {
    if (checkoutTreeAnalyticsLoad.pending && !checkoutTreeAnalyticsLoad.value) {
      return { status: "pending" };
    } else if (!checkoutTreeAnalyticsLoad.value) {
      return { status: "metrics-not-applicable" };
    } else if (!analyticsForCheckoutNodeByNodeId[nodeId]) {
      return { status: "metrics-not-available" };
    } else {
      return {
        status: "display-metrics",
        value: analyticsForCheckoutNodeByNodeId[nodeId].total,
      };
    }
  }, [analyticsForCheckoutNodeByNodeId, checkoutTreeAnalyticsLoad, nodeId]);

  return { metrics };
}
